import { memo, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  ChevronUp,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Quote,
  Redo,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo
} from 'lucide-react';
import type { Editor } from '@tiptap/react';

interface FormattingToolbarProps {
  editor: Editor;
  isToolbarExpanded: boolean;
  onToggleToolbar: () => void;
  onToolbarExpandedChange: (open: boolean) => void;
}

export const FormattingToolbar = memo(function FormattingToolbar({
  editor,
  isToolbarExpanded,
  onToggleToolbar,
  onToolbarExpandedChange,
}: FormattingToolbarProps) {
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const allItems = useMemo(
    () => [
      {
        key: 'bold',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('bold')}
            onPressedChange={() => editor.chain().focus().toggleBold().run()}
            data-testid="button-bold"
            aria-label="Bold"
          >
            <Bold className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'italic',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('italic')}
            onPressedChange={() => editor.chain().focus().toggleItalic().run()}
            data-testid="button-italic"
            aria-label="Italic"
          >
            <Italic className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'underline',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('underline')}
            onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
            data-testid="button-underline"
            aria-label="Underline"
          >
            <UnderlineIcon className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-1', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'heading1',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 1 })}
            onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            data-testid="button-heading1"
            aria-label="Heading 1"
          >
            <Heading1 className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'heading2',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 2 })}
            onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            data-testid="button-heading2"
            aria-label="Heading 2"
          >
            <Heading2 className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'heading3',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('heading', { level: 3 })}
            onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            data-testid="button-heading3"
            aria-label="Heading 3"
          >
            <Heading3 className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-2', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'bulletList',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('bulletList')}
            onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
            data-testid="button-bullet-list"
            aria-label="Bullet List"
          >
            <List className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'orderedList',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('orderedList')}
            onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
            data-testid="button-ordered-list"
            aria-label="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-3', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'strike',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('strike')}
            onPressedChange={() => editor.chain().focus().toggleStrike().run()}
            data-testid="button-strikethrough"
            aria-label="Strikethrough"
          >
            <Strikethrough className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'highlight',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('highlight')}
            onPressedChange={() => editor.chain().focus().toggleHighlight().run()}
            data-testid="button-highlight"
            aria-label="Highlight"
          >
            <Highlighter className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-4', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'blockquote',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('blockquote')}
            onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}
            data-testid="button-blockquote"
            aria-label="Quote"
          >
            <Quote className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'codeBlock',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive('codeBlock')}
            onPressedChange={() => editor.chain().focus().toggleCodeBlock().run()}
            data-testid="button-code-block"
            aria-label="Code Block"
          >
            <Code className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-5', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'alignLeft',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive({ textAlign: 'left' })}
            onPressedChange={() => editor.chain().focus().setTextAlign('left').run()}
            data-testid="button-align-left"
            aria-label="Align Left"
          >
            <AlignLeft className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'alignCenter',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive({ textAlign: 'center' })}
            onPressedChange={() => editor.chain().focus().setTextAlign('center').run()}
            data-testid="button-align-center"
            aria-label="Align Center"
          >
            <AlignCenter className="h-4 w-4" />
          </Toggle>
        )
      },
      {
        key: 'alignRight',
        render: (
          <Toggle
            size="sm"
            pressed={editor.isActive({ textAlign: 'right' })}
            onPressedChange={() => editor.chain().focus().setTextAlign('right').run()}
            data-testid="button-align-right"
            aria-label="Align Right"
          >
            <AlignRight className="h-4 w-4" />
          </Toggle>
        )
      },
      { key: 'sep-6', isSeparator: true, render: <Separator orientation="vertical" className="mx-1 h-6 shrink-0" /> },
      {
        key: 'undo',
        render: (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            data-testid="button-undo"
            className="h-8 w-8"
            aria-label="Undo"
          >
            <Undo className="h-4 w-4" />
          </Button>
        )
      },
      {
        key: 'redo',
        render: (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            data-testid="button-redo"
            className="h-8 w-8"
            aria-label="Redo"
          >
            <Redo className="h-4 w-4" />
          </Button>
        )
      }
    ],
    [editor]
  );

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => allItems.map((item) => item.key));
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  const itemByKey = useMemo(() => {
    return allItems.reduce<Record<string, { key: string; render: ReactNode; isSeparator?: boolean }>>((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {});
  }, [allItems]);

  const normalizeKeys = (keys: string[]) => {
    const normalized: string[] = [];
    for (const key of keys) {
      const isSeparator = itemByKey[key]?.isSeparator;
      if (isSeparator && (normalized.length === 0 || itemByKey[normalized[normalized.length - 1]]?.isSeparator)) {
        continue;
      }
      normalized.push(key);
    }
    while (normalized.length && itemByKey[normalized[0]]?.isSeparator) normalized.shift();
    while (normalized.length && itemByKey[normalized[normalized.length - 1]]?.isSeparator) normalized.pop();
    return normalized;
  };

  useLayoutEffect(() => {
    const container = itemsContainerRef.current;
    if (!container) return;

    const calculate = () => {
      const available = container.clientWidth;
      const computed = window.getComputedStyle(container);
      const gap = parseFloat(computed.columnGap || computed.gap || '0') || 0;
      let used = 0;
      const nextVisible: string[] = [];
      const nextHidden: string[] = [];

      allItems.forEach((item) => {
        const el = itemRefs.current[item.key];
        const width = el?.offsetWidth ?? 0;
        const nextUsed = used + (nextVisible.length ? gap : 0) + width;
        if (nextUsed <= available) {
          used = nextUsed;
          nextVisible.push(item.key);
        } else {
          nextHidden.push(item.key);
        }
      });

      const cleanedVisible = normalizeKeys(nextVisible);
      const cleanedHidden = normalizeKeys(nextHidden);

      setVisibleKeys((prev) => (prev.join('|') === cleanedVisible.join('|') ? prev : cleanedVisible));
      setHiddenKeys((prev) => (prev.join('|') === cleanedHidden.join('|') ? prev : cleanedHidden));
    };

    const resizeObserver = new ResizeObserver(calculate);
    resizeObserver.observe(container);
    if (rowRef.current) resizeObserver.observe(rowRef.current);
    if (toggleRef.current) resizeObserver.observe(toggleRef.current);
    calculate();

    return () => resizeObserver.disconnect();
  }, [allItems, itemByKey]);

  const hasHiddenItems = hiddenKeys.length > 0;
  return (
    <>
      {/* Primary Toolbar - shrink-0 so it stays fixed; only EditorContent below scrolls */}
      <div className="shrink-0 p-2 border-b bg-muted/50" data-testid="editor-toolbar">
        <Collapsible open={isToolbarExpanded} onOpenChange={onToolbarExpandedChange} className="relative">
          <div ref={rowRef} className="flex items-center gap-1">
            <div ref={itemsContainerRef} className="flex flex-nowrap items-center gap-1 overflow-hidden min-w-0 flex-1">
              {allItems.map((item) => {
                if (!visibleKeys.includes(item.key)) return null;
                return (
                  <div key={item.key}>
                    {item.render}
                  </div>
                );
              })}
            </div>

            {/* Expand/Collapse button inline */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 shrink-0"
              onClick={onToggleToolbar}
              data-testid="button-toggle-toolbar"
              aria-label={isToolbarExpanded ? "Hide additional formatting options" : "Show additional formatting options"}
              disabled={!hasHiddenItems}
              ref={toggleRef}
            >
              {isToolbarExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          <CollapsibleContent className="absolute right-0 top-full mt-1 z-10">
            <div className="flex flex-wrap items-center gap-1 p-2 border rounded-md bg-background shadow-md">
              {hiddenKeys.length === 0 && (
                <div className="text-xs text-muted-foreground px-2 py-1">No additional items</div>
              )}
              {hiddenKeys.map((key) => {
                const item = itemByKey[key];
                if (!item) return null;
                return (
                  <div key={key}>
                    {item.render}
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>

          <div className="absolute left-[-9999px] top-[-9999px] flex items-center gap-1">
            {allItems.map((item) => (
              <div key={item.key} ref={(el) => { itemRefs.current[item.key] = el; }}>
                {item.render}
              </div>
            ))}
          </div>
        </Collapsible>
      </div>
    </>
  );
});
